import { ChangeEvent, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { shared } from 'src/core/themes';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/modals/Modal';
import { ChargeOrCreditReasonSelector } from 'src/modals/SaleDetails/components/PaymentSection/SaleLineItemModal/ChargeOrCreditReason/ChargeOrCreditReasonSelector';
import { ContentId } from 'src/utils/constants/contentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import {
  SaleCostType,
  SaleLineItem,
  SaleLineItemStatus,
} from 'src/WebApiController';

import * as styles from './SaleLineItemModal.css';
import { AddLineItemModalBodyProps as SaleLineItemModalBodyProps } from './SaleLineItemModal.types';

export const SaleLineItemModal = (props: SaleLineItemModalBodyProps) => {
  const methods = useForm<SaleLineItem>({
    defaultValues: props.item ?? {
      description: null,
      costType: SaleCostType.Charge,
      status: SaleLineItemStatus.Unpaid,
      costDate: props.costDate ?? new Date().toISOString(),
      dueDate: new Date().toISOString(),
      currencyCode: props.currencyCode,
      saleLineItemChargeReason: props.saleLineItemChargeReason,
      saleLineItemCreditReason: props.saleLineItemCreditReason,
      cost: props.remainingBalance ?? undefined,
    },
  });

  useEffect(() => {
    if (props.item && !isEqual(props.item, methods.formState.defaultValues)) {
      methods.reset(props.item);
    } else if (!props.item) {
      methods.reset({
        description: null,
        costType: SaleCostType.Charge,
        status: SaleLineItemStatus.Unpaid,
        costDate: new Date().toISOString(),
        dueDate: new Date().toISOString(),
        currencyCode: props.currencyCode,
        saleLineItemCreditReason: null,
        saleLineItemChargeReason: null,
      });
    }
  }, [methods, props.currencyCode, props.item, props.isOpen]);

  return (
    <FormProvider {...methods}>
      <SaleLineItemModalBody {...methods} {...props} />
    </FormProvider>
  );
};

const SaleLineItemModalBody = ({
  isOpen,
  onClose,
  onSave,
  currencyCode,
}: SaleLineItemModalBodyProps & UseFormReturn<SaleLineItem>) => {
  const itemNameText = useContent(ContentId.ItemName);
  const amountText = useContent(ContentId.Amount);
  const requiredText = useContent(ContentId.Required);

  const { control, reset, register, watch, setValue, handleSubmit, formState } =
    useFormContext<SaleLineItem>();

  const name = watch('description');
  const cost = watch('cost');
  const costType = watch('costType');
  const status = watch('status');
  const costDate = watch('costDate');
  const dueDate = watch('dueDate');

  const { getUiCurrency } = useLocalizationContext();

  const uiCurrency = useMemo(
    () => getUiCurrency(currencyCode),
    [getUiCurrency, currencyCode]
  );

  return (
    <Modal isOpen={isOpen} size="sm" centered>
      <ModalHeader onClose={onClose}>
        <div className={styles.headerBar}>
          <h5 className={`${shared.typography.title5} ${styles.headerText}`}>
            <Content id={ContentId.AddChargeOrCredit} />
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <Stack direction="column" className={styles.bodyContainer} gap="l">
          <PosFormField
            label={itemNameText}
            errors={formState.errors.description?.message}
          >
            <PosTextField
              rootProps={{
                state: getTextFieldState(formState.errors.description),
              }}
              {...register('description', { required: requiredText })}
              value={name ?? ''}
              trimOnBlur={true}
            />
          </PosFormField>
          <RadioGroup
            className={styles.radioGroup}
            value={costType}
            onValueChange={(v) => setValue('costType', v as SaleCostType)}
          >
            <Radio
              label={<Content id={ContentId.Charge} />}
              value={SaleCostType.Charge}
            />
            <Radio
              label={<Content id={ContentId.Credit} />}
              value={SaleCostType.Credit}
            />
          </RadioGroup>

          <ChargeOrCreditReasonSelector />

          <PosFormField
            label={amountText}
            errors={formState.errors.cost?.message}
          >
            <PosCurrencyField
              rootProps={{
                state: getTextFieldState(formState.errors.cost),
              }}
              value={cost}
              uiCurrency={uiCurrency}
              {...register('cost', {
                required: !(cost > 0) ? requiredText : undefined,
                onBlur: (e: ChangeEvent<HTMLInputElement>) => {
                  setValue('cost', parseFloat(e.target.value));
                },
              })}
            />
          </PosFormField>

          <Stack gap="l">
            <PosFormField
              label={<Content id={ContentId.DateIncurred} />}
              errors={formState.errors.costDate?.message}
            >
              <Controller
                name={'costDate'}
                rules={{ required: !costDate ? requiredText : undefined }}
                control={control}
                render={({ field: { ...field } }) => (
                  <DatePickerInput
                    fieldError={formState.errors.costDate?.message}
                    locale={getLocaleFromLanguageOrCurrent()}
                    {...field}
                    onDateChange={(d) => {
                      setValue(field.name, d.toISOString());
                    }}
                  />
                )}
              />
            </PosFormField>
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <CancelButton
          textContentId={ContentId.Cancel}
          onClick={() => {
            onClose();
            reset();
          }}
        />
        <OkButton
          textContentId={ContentId.Save}
          onClick={() => {
            handleSubmit(onSave)();
          }}
        />
      </ModalFooter>
    </Modal>
  );
};
